import * as React from "react"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 1000 // atraso p/ remover do DOM após fechar (animação)
const AUTO_DISMISS_DELAY = 5000 // auto-fechar toasts comuns; erros ficam fixos até o X

type ToasterToast = {
    id: string
    title?: React.ReactNode
    description?: React.ReactNode
    action?: React.ReactNode
    variant?: "default" | "destructive"
    open?: boolean
}
let count = 0
function genId() {
    count = (count + 1) % Number.MAX_VALUE
    return count.toString()
}

type Action =
    | { type: "ADD_TOAST"; toast: ToasterToast }
    | { type: "UPDATE_TOAST"; toast: Partial<ToasterToast> }
    | { type: "DISMISS_TOAST"; toastId?: string }
    | { type: "REMOVE_TOAST"; toastId?: string }

interface State {
    toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
    if (toastTimeouts.has(toastId)) return

    const timeout = setTimeout(() => {
        toastTimeouts.delete(toastId)
        dispatch({ type: "REMOVE_TOAST", toastId })
    }, TOAST_REMOVE_DELAY)

    toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case "ADD_TOAST":
            return {
                ...state,
                toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
            }

        case "UPDATE_TOAST":
            return {
                ...state,
                toasts: state.toasts.map((t) =>
                    t.id === action.toast.id ? { ...t, ...action.toast } : t
                ),
            }

        case "DISMISS_TOAST": {
            const { toastId } = action

            if (toastId) addToRemoveQueue(toastId)
            else state.toasts.forEach((t) => addToRemoveQueue(t.id))

            return {
                ...state,
                toasts: state.toasts.map((t) =>
                    toastId === undefined || t.id === toastId
                        ? { ...t, open: false }
                        : t
                ),
            }
        }

        case "REMOVE_TOAST":
            if (!action.toastId) return { ...state, toasts: [] }
            return {
                ...state,
                toasts: state.toasts.filter((t) => t.id !== action.toastId),
            }
    }
}

const listeners: Array<(state: State) => void> = []
let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
    memoryState = reducer(memoryState, action)
    listeners.forEach((listener) => listener(memoryState))
}

type ToastOptions = Omit<ToasterToast, "id">

function toast(props: ToastOptions) {
    const id = genId()

    const update = (toast: ToasterToast) =>
        dispatch({ type: "UPDATE_TOAST", toast: { ...toast, id } })

    const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

    dispatch({
        type: "ADD_TOAST",
        toast: {
            ...props,
            id,
            open: true,
        },
    })

    // Erros (destructive) ficam fixos até o usuário fechar no X; os demais somem sozinhos.
    if (props.variant !== "destructive") {
        setTimeout(() => dismiss(), AUTO_DISMISS_DELAY)
    }

    return { id, dismiss, update }
}

function useToast() {
    const [state, setState] = React.useState<State>(memoryState)

    React.useEffect(() => {
        listeners.push(setState)
        return () => {
            const index = listeners.indexOf(setState)
            if (index > -1) listeners.splice(index, 1)
        }
    }, [])

    return {
        ...state,
        toast,
        dismiss: (toastId?: string) =>
            dispatch({ type: "DISMISS_TOAST", toastId }),
    }
}

export { useToast, toast }
